import React, { useState, useEffect } from 'react';

import DatePicker from 'react-date-picker';
import 'react-day-picker/lib/style.css';

import { apiGet, getAccountsApiUrl } from '../common/AppApi';
import Strings from '../common/Strings';
import MessageTag from '../common/MessageTag';
import Processing from '../common/Processing';
import Pagination, { resetPaging } from '../common/Pagination';
import { PageSize } from '../common/Constants';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';


/*SettlementReport Screen: Displays a report of settlements based on selected program and date range. */
export default function SettlementReport() {
	let [programs, setPrograms] = useState([]);
	let [startDate, setStartDate] = useState(null);
	let [endDate, setEndDate] = useState(null);
	let [message, setMessage] = useState();
	let [showSpinner, setSpinner] = useState(false);
	let [results, setResults] = useState(null);
	let [pagingData, setPagingData] = useState(resetPaging());

	useEffect(() => {
		apiGet(getAccountsApiUrl() + '/dash/programs').then((resp) => {
			setPrograms(resp || []);
		}).catch((err) => {
			console.error(err);
		});
	}, []);

	let formatDate = (date) => {
		let d = new Date(date);
		let month = String(d.getMonth() + 1).padStart(2, '0');
		let day = String(d.getDate()).padStart(2, '0');
		return d.getFullYear() + '-' + month + '-' + day;
	};

	let handleDisplayReport = () => {
		if (!startDate || !endDate) {
			setMessage({ text: 'Please select both a start date and an end date.', type: 'warning' });
			return;
		}
		if (endDate < startDate) {
			setMessage({ text: 'End date must be on or after the start date.', type: 'warning' });
			return;
		}

		// Read programId directly from the DOM — guaranteed to be the current selected value
		let selectedProgramId = document.getElementById('settlementProgramId').value;

		let queryParams = [];
		if (selectedProgramId) {
			queryParams.push('programId=' + selectedProgramId);
		}
		queryParams.push('startDate=' + formatDate(startDate));
		queryParams.push('endDate=' + formatDate(endDate));

		setMessage(null);
		setSpinner(true);

		let url = getAccountsApiUrl() + '/dash/settlement-report?' + queryParams.join('&');
		apiGet(url).then((resp) => {
			if (!resp || resp.length === 0) {
				setResults([]);
			} else {
				setResults(resp);
				setPagingData(resetPaging());
			}
		}).catch((err) => {
			console.error(err);
			let messageObject = { text: Strings.error.general, type: 'danger' };
			if (err.privilegeError) {
				messageObject.text = <>{err.message}</>;
			}
			setMessage(messageObject);
		}).finally(() => setSpinner(false));
	};

	let columns = [
		{ key: 'program',        label: 'Program' },
		{ key: 'procId',         label: 'Proc ID' },
		{ key: 'merchant',       label: 'Merchant' },
		{ key: 'trxCount',       label: 'Trx Count' },
		{ key: 'settlementDate', label: 'Settlement Date' },
		{ key: 'amount',         label: 'Amount' },
	];

	let nextPage = () => setPagingData({ ...pagingData, currentPage: pagingData.currentPage + 1 });
	let prevPage = () => setPagingData({ ...pagingData, currentPage: pagingData.currentPage - 1 });
	let gotoPage = (pageNo) => setPagingData({ ...pagingData, currentPage: pageNo });

	let exportExcel = () => {
		let rows = results.map((row) =>
			columns.reduce((obj, col) => {
				obj[col.label] = row[col.key] != null ? row[col.key] : '';
				return obj;
			}, {})
		);
		let ws = XLSX.utils.json_to_sheet(rows);
		let wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Settlement Report');
		XLSX.writeFile(wb, 'settlement-report.xlsx');
	};

	let exportPdf = () => {
		let doc = new jsPDF({ orientation: 'landscape' });
		doc.text('Settlement Report', 14, 14);
		doc.autoTable({
			startY: 20,
			headStyles: { fillColor: [52, 58, 64] },
			head: [columns.map((col) => col.label)],
			body: results.map((row) =>
				columns.map((col) => (row[col.key] != null ? String(row[col.key]) : ''))
			),
		});
		doc.save('settlement-report.pdf');
	};

	let ResultsTable = () => {
		if (results === null) return null;

		if (results.length === 0) {
			return (
				<div className="alert alert-info mt-3">
					No results found for the selected program and date range.
				</div>
			);
		}

		let pageStart = (pagingData.currentPage - 1) * PageSize;
		let pageRows = results.slice(pageStart, pageStart + PageSize);
		let pageCount = Math.ceil(results.length / PageSize);

		return (
			<>
				<div className="table-responsive mt-3">
					<table className="table table-bordered table-striped table-sm">
						<thead className="thead-dark">
							<tr>
								{columns.map((col) => (
									<th key={col.key}>{col.label}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{pageRows.map((row, index) => (
								<tr key={index}>
									{columns.map((col) => (
										<td key={col.key}>{row[col.key] != null ? String(row[col.key]) : ''}</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<div className="mt-2 mb-2">
					Export:&nbsp;
					<button className="btn btn-sm btn-primary" onClick={exportExcel}>Excel</button>
					&nbsp;
					<button className="btn btn-sm btn-primary" onClick={exportPdf}>PDF</button>
				</div>
				<Pagination recordCount={results.length} pageCount={pageCount} pagingData={pagingData}
					setPagingData={() => {}} pageSize={PageSize}
					nextPage={nextPage} prevPage={prevPage} gotoPage={gotoPage} />
			</>
		);
	};

	return (
		<div className="container mt-4">
			<h4>Settlement Report</h4>
			<hr />
			<Processing show={showSpinner} />
			<MessageTag message={message} />

			<div className="form-row align-items-end">
				<div className="form-group col-md-3">
					<label htmlFor="settlementProgramId">Program</label>
					<select id="settlementProgramId" className="form-control" defaultValue="">
						<option value="">All Programs</option>
						{programs.map((p) => (
							<option key={p.programId} value={p.programId}>{p.programName}</option>
						))}
					</select>
				</div>
				<div className="form-group col-md-3">
					<label htmlFor="startDate">Start Date</label>
					<DatePicker
						id="startDate"
						name="startDate"
						value={startDate}
						onChange={(val) => setStartDate(val)}
						className="form-control"
						format="yyyy-MM-dd"
						monthPlaceholder="MM"
						yearPlaceholder="YYYY"
						dayPlaceholder="DD"
					/>
				</div>
				<div className="form-group col-md-3">
					<label htmlFor="endDate">End Date</label>
					<DatePicker
						id="endDate"
						name="endDate"
						value={endDate}
						onChange={(val) => setEndDate(val)}
						className="form-control"
						format="yyyy-MM-dd"
						monthPlaceholder="MM"
						yearPlaceholder="YYYY"
						dayPlaceholder="DD"
					/>
				</div>
				<div className="form-group col-md-2">
					<button
						className="btn btn-primary"
						onClick={handleDisplayReport}
						disabled={showSpinner}
					>
						Display Report
					</button>
				</div>
			</div>

			<ResultsTable />
		</div>
	);
}
